// @ts-nocheck
import {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {z} from "zod";
import {BaseButton, BaseCheckbox, BaseDatePicker, BaseInput, BaseSelect} from "@/components";
import {PERSISTENT_COOKIE_OPTIONS, setStorage} from "@/store/util.store";
import {getMetaPixelParams} from "@/util/urlParams";
import eyeActive from "../../assets/images/icon-eye-active.svg";
import eyeInactive from "../../assets/images/icon-eye-inactive.svg";
import useUserRegistration from "../../hooks/useUserRegistration";
import {useAuthStore} from "../../store/useAuth.store";
import {useLogo} from "../../util/logoUtils";

const mobileRegistrationContainerCls =
  "min-h-screen w-screen bg-[var(--background)] flex flex-col justify-start items-center p-0 m-0 fixed top-0 left-0 z-[var(--z-modal)] overflow-y-auto box-border portrait:h-[100svh] landscape:h-[100svh] landscape:justify-center";
const mobileRegistrationContentCls =
  "w-full max-w-full flex flex-col justify-start items-center min-h-screen p-5 box-border max-md:p-[var(--space-3-75)] max-[426px]:p-2.5 landscape:flex-row landscape:items-start landscape:justify-around landscape:p-5 landscape:min-h-[auto]";
const logoContainerCls =
  "flex justify-center items-center mb-[30px] w-full pt-5 max-[426px]:mb-[25px] max-[426px]:pt-[var(--space-3-75)] landscape:mb-0 landscape:mr-10 landscape:shrink-0 landscape:pt-0 landscape:self-center";
const logoCls =
  "w-40 h-auto max-w-[var(--size-50)] max-md:w-[140px] max-[426px]:w-[var(--size-30)] landscape:w-[var(--size-30)]";
const formContainerCls =
  "w-full max-w-[var(--size-100)] flex justify-center items-center flex-1 max-md:max-w-full landscape:flex-1 landscape:max-w-[var(--size-100)] landscape:self-start";
const formCls = "w-full";
const formInnerCls = "w-full flex flex-col items-center";
const titleCls =
  "font-[var(--font-family)] text-2xl leading-[var(--leading-xl)] text-center font-extrabold mb-6 text-[var(--text-color)] max-md:text-2xl max-md:mb-5 max-[426px]:text-[var(--font-h2)] max-[426px]:mb-[var(--space-4-75)]";
const inputCls =
  "flex items-center justify-center w-full h-12 rounded-full bg-[var(--modal-background)] text-base text-[var(--placeholder)] pl-6 border border-[var(--custom-navbar-hover)] max-[426px]:h-11 max-[426px]:pl-5 [&_input::placeholder]:text-base [&_input::placeholder]:text-left [&_input::placeholder]:opacity-100 [&_input::placeholder]:font-medium [&_input::placeholder]:text-[var(--alternative-white)]";
const selectCls =
  "w-full h-12 rounded-full bg-[var(--modal-background)] border border-[var(--custom-navbar-hover)] max-[426px]:h-11 [&_.ant-select-selector]:!h-12 [&_.ant-select-selector]:!rounded-full [&_.ant-select-selector]:!bg-[var(--modal-background)] [&_.ant-select-selector]:!border-0 [&_.ant-select-selector]:!pl-6 [&_.ant-select-selector]:!flex [&_.ant-select-selector]:!items-center max-[426px]:[&_.ant-select-selector]:!h-11 max-[426px]:[&_.ant-select-selector]:!pl-5 [&_.ant-select-selection-placeholder]:!text-[var(--alternative-white)] [&_.ant-select-selection-placeholder]:!text-base [&_.ant-select-selection-placeholder]:!font-medium";
const checkboxTextCls =
  "text-[var(--text-color)] text-sm font-normal leading-[1.4] max-[426px]:text-[var(--font-body)]";
const errorMessageCls = "text-center mb-[var(--space-3-75)] w-full";
const submitButtonCls =
  "font-[var(--font-family)] text-[var(--font-body-lg)] font-bold rounded-full w-full h-12 bg-[var(--button-background)] border-0 hover:bg-[var(--button-hover)] disabled:bg-[var(--placeholder)] disabled:opacity-60";
const loginTextCls = "cursor-default font-[var(--font-family)] text-[var(--text-color)] text-sm";
const loginLinkCls =
  "font-[var(--font-family)] ml-[var(--space-1-5)] text-[var(--button-background)] no-underline cursor-pointer text-sm hover:text-[var(--button-hover)]";
const loadingContainerCls =
  "min-h-screen w-screen bg-[var(--modal-background)] flex flex-col justify-center items-center fixed top-0 left-0 z-[var(--z-modal)]";
const loadingLogoCls = "w-[var(--size-30)] h-auto mb-[30px]";
const loadingSpinnerCls =
  "w-10 h-10 border-4 border-[var(--placeholder)] border-t-4 border-t-[var(--button-background)] rounded-full animate-spin";

const SPECIAL_CHAR_REGEX = /[´ñáéíóúáèìòùâêîôûäëïöüãõ¨';[(){}¡!"ª°·#$%^&*+=\\|~<>¿?]/;
const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,63}$/;

const yearsBetween = (fromISO, toISO) => {
  const from = new Date(fromISO);
  const to = new Date(toISO);
  let age = to.getFullYear() - from.getFullYear();
  const m = to.getMonth() - from.getMonth();
  if (m < 0 || (m === 0 && to.getDate() < from.getDate())) age -= 1;
  return age;
};

const buildSchema = t =>
  z
    .object({
      email: z
        .string()
        .trim()
        .min(1, t("registrationEmail_required"))
        .superRefine((email, ctx) => {
          if (SPECIAL_CHAR_REGEX.test(email)) {
            ctx.addIssue({code: "custom", message: t("registrationEmail_specialChar")});
            return;
          }
          if (!EMAIL_REGEX.test(email)) {
            ctx.addIssue({code: "custom", message: t("registrationEmail_message")});
            return;
          }
          const [localPart, domainPart] = email.split("@");
          if (
            localPart.startsWith(".") ||
            localPart.endsWith(".") ||
            localPart.startsWith("-") ||
            localPart.startsWith("+") ||
            localPart.endsWith("-") ||
            localPart.endsWith("+")
          ) {
            ctx.addIssue({code: "custom", message: t("registrationEmail_CharStartEnd")});
            return;
          }
          if (
            localPart.includes("..") ||
            localPart.includes("--") ||
            domainPart.includes("..") ||
            domainPart.includes("--")
          ) {
            ctx.addIssue({code: "custom", message: t("registrationEmail_CharConsecutive")});
            return;
          }
          for (const part of domainPart.split(".")) {
            if (part.startsWith("-") || part.endsWith("-")) {
              ctx.addIssue({code: "custom", message: t("registrationEmail_CharStartEnd")});
              return;
            }
          }
        }),
      birthDate: z
        .string()
        .min(1, t("registrationSelectDate"))
        .refine(value => yearsBetween(value, new Date().toISOString()) >= 18, t("registrationBirthdate_message")),
      password: z.string().min(1, t("registrationPassword")).min(8, t("registrationPasswordMinLength")),
      repeat_password: z.string().min(1, t("registrationRepeatPassword_required")),
      preferred_bonus: z.string().optional(),
      terms: z.literal(true, {errorMap: () => ({message: t("registrationAcceptTerms")})}),
    })
    .refine(data => data.password === data.repeat_password, {
      message: t("registrationPasswordNotMatch"),
      path: ["repeat_password"],
    });

const MobileRegistration = () => {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const logo = useLogo();

  const authStatus = useAuthStore(state => state.authStatus);
  const validateSession = useAuthStore(state => state.validateSession);

  const {mutate: postUserRegistration, isPending, error} = useUserRegistration();

  const [values, setValues] = useState({
    email: "",
    birthDate: "",
    password: "",
    repeat_password: "",
    preferred_bonus: "slot",
    terms: false,
  });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showRepeatPassword, setShowRepeatPassword] = useState(false);
  const [registerButtonClicked, setRegisterButtonClicked] = useState(false);

  useEffect(() => {
    validateSession();
    setStorage("isMobileLoginUser", "true", PERSISTENT_COOKIE_OPTIONS);
  }, [validateSession]);

  useEffect(() => {
    if (authStatus === "authenticated") navigate("/", {replace: true});
  }, [authStatus, navigate]);

  const setField = (field, value) => {
    setValues(v => ({...v, [field]: value}));
    if (errors[field]) setErrors(prev => ({...prev, [field]: undefined}));
  };

  const handleInput = field => e => setField(field, e.target.value);

  const handleSubmit = e => {
    e.preventDefault();
    setRegisterButtonClicked(true);
    const result = buildSchema(t).safeParse(values);
    if (!result.success) {
      const nextErrors = {};
      for (const issue of result.error.issues) {
        const key = issue.path[0];
        if (typeof key === "string" && !nextErrors[key]) nextErrors[key] = issue.message;
      }
      setErrors(nextErrors);
      return;
    }
    setErrors({});
    const {fbp, fbc} = getMetaPixelParams();
    const sendData = {
      birth_date: result.data.birthDate,
      email: result.data.email,
      password: result.data.password,
      preferred_bonus: result.data.preferred_bonus || "slot",
    };
    if (fbp) sendData.fbp = fbp;
    if (fbc) sendData.fbc = fbc;
    setStorage("isMobileLoginUser", "true");
    postUserRegistration(sendData);
  };

  if (authStatus === "checking") {
    return (
      <div className={loadingContainerCls}>
        <img
          src={logo}
          alt="logo"
          className={loadingLogoCls}
        />
        <div className={loadingSpinnerCls}></div>
      </div>
    );
  }

  const renderPasswordToggle = (visible, onToggle) => (
    <button
      type="button"
      onClick={onToggle}
      aria-label="toggle password"
      style={{background: "transparent", border: "none", padding: 0, cursor: "pointer"}}
    >
      <img
        src={visible ? eyeActive : eyeInactive}
        alt="toggle password"
        style={{width: "var(--size-6)", height: "var(--size-6)"}}
      />
    </button>
  );

  return (
    <div className={mobileRegistrationContainerCls}>
      <div className={mobileRegistrationContentCls}>
        <div className={logoContainerCls}>
          <img
            src={logo}
            alt="logo"
            className={logoCls}
          />
        </div>

        <div className={formContainerCls}>
          <form
            className={formCls}
            onSubmit={handleSubmit}
            autoComplete="off"
            noValidate
          >
            <div className={formInnerCls}>
              <h3 className={titleCls}>{t("baseModalTitleRegistration")}</h3>

              <div style={{width: "100%", marginBottom: "var(--space-3-75)"}}>
                <BaseInput
                  placeholder={t("registrationEmail")}
                  value={values.email}
                  onChange={handleInput("email")}
                  error={errors.email}
                  containerClassName={inputCls}
                />
              </div>

              <div style={{width: "100%", marginBottom: "var(--space-3-75)"}}>
                <BaseDatePicker
                  type="date"
                  placeholder={t("registrationSelectDate")}
                  value={values.birthDate}
                  onChange={value => setField("birthDate", value ?? "")}
                  error={errors.birthDate}
                  containerClassName={inputCls}
                />
              </div>

              <div style={{width: "100%", marginBottom: "var(--space-3-75)"}}>
                <BaseInput
                  type={showPassword ? "text" : "password"}
                  placeholder={t("registrationPassword")}
                  value={values.password}
                  onChange={handleInput("password")}
                  error={errors.password}
                  containerClassName={inputCls}
                  rightIcon={renderPasswordToggle(showPassword, () => setShowPassword(v => !v))}
                />
              </div>

              <div style={{width: "100%", marginBottom: "var(--space-3-75)"}}>
                <BaseInput
                  type={showRepeatPassword ? "text" : "password"}
                  placeholder={t("registrationRepeatPassword")}
                  value={values.repeat_password}
                  onChange={handleInput("repeat_password")}
                  error={errors.repeat_password}
                  containerClassName={inputCls}
                  rightIcon={renderPasswordToggle(showRepeatPassword, () => setShowRepeatPassword(v => !v))}
                />
              </div>

              <div style={{width: "100%", marginBottom: "var(--space-3-75)"}}>
                <BaseSelect
                  className={selectCls}
                  placeholder={t("registrationBonusTitle")}
                  value={values.preferred_bonus}
                  onChange={e => setField("preferred_bonus", e.target.value)}
                  options={[
                    {value: "slot", label: t("registrationFormBonusSlot")},
                    {value: "casino_live", label: t("registrationFormBonusCasino")},
                    {value: "parley", label: t("registrationFormBonusParley")},
                  ]}
                />
              </div>

              <div style={{width: "100%", marginBottom: "var(--space-5)"}}>
                <BaseCheckbox
                  checked={values.terms}
                  onChange={checked => setField("terms", checked)}
                >
                  <span className={checkboxTextCls}>
                    Acepto los términos y condiciones. Y ser mayor de 18 años de edad.
                  </span>
                </BaseCheckbox>
                {errors.terms && (
                  <div
                    style={{
                      marginTop: "var(--space-2)",
                      color: "var(--color-danger-text)",
                      fontSize: "var(--font-body)",
                    }}
                  >
                    {errors.terms}
                  </div>
                )}
              </div>

              {registerButtonClicked && error && (
                <div className={errorMessageCls}>
                  <span style={{color: "var(--color-danger-text)"}}>
                    {error?.response?.data?.message || error?.message || "Error en el registro"}
                  </span>
                </div>
              )}

              <div style={{textAlign: "center", marginBottom: "var(--space-5)"}}>
                <BaseButton
                  type="submit"
                  variant="primary"
                  disabled={!values.terms}
                  loading={isPending}
                  className={submitButtonCls}
                >
                  {t("registrationAcceptTermsRegister")}
                </BaseButton>
              </div>

              <div style={{textAlign: "center"}}>
                <span className={loginTextCls}>
                  ¿Ya tienes una cuenta?{" "}
                  <button
                    type="button"
                    onClick={() => navigate("/mobile-login")}
                    className={loginLinkCls}
                    style={{
                      background: "transparent",
                      border: "none",
                      cursor: "pointer",
                      padding: 0,
                      color: "var(--color-primary)",
                    }}
                  >
                    Iniciar sesión
                  </button>
                </span>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default MobileRegistration;
